<template>
  <div>
    <div v-if="isFooterOnly" class="buttons is-right">
      <button class="button" :disabled="isSaving" @click="closeTopModal">
        Close</button
      ><button
        class="button is-success"
        :class="{ 'is-loading': isSaving }"
        :disabled="isSaving"
        @click="$root.$emit(`component-save-${modalIndex}`)"
      >
        Save component
      </button>
    </div>
    <div v-else>
      <div
        v-if="typesError"
        class="notification is-danger is-light"
        role="alert"
      >
        Could not load component types: {{ typesError }}
        <button
          class="button is-small"
          @click="$store.dispatch('loadComponentTypes')"
        >
          Retry
        </button>
      </div>
      <div v-if="error" class="notification is-danger is-light" role="alert">
        {{ error }}
      </div>
      <p v-if="loading" role="status">Loading component...</p>
      <form v-else @submit.prevent="save">
        <p v-if="modalData.inventoryId" class="notification is-info is-light">
          The new component will be installed in this camera when saved.
        </p>
        <div class="columns is-multiline">
          <div class="column is-half">
            <label class="label" :for="`type-${modalIndex}`">Type</label>
            <div class="select is-fullwidth">
              <select :id="`type-${modalIndex}`" v-model="form.type" required>
                <option value="" disabled>Select a type</option>
                <option v-for="type in types" :key="type">{{ type }}</option>
              </select>
            </div>
          </div>
          <div class="column is-half">
            <label class="label" :for="`status-${modalIndex}`">Status</label>
            <div class="select is-fullwidth">
              <select
                :id="`status-${modalIndex}`"
                v-model="form.status"
                :disabled="!!activeInstallation || !!modalData.inventoryId"
              >
                <option
                  v-for="status in statuses"
                  :key="status"
                  :disabled="status === 'installed' && !activeInstallation"
                >
                  {{ status }}
                </option>
              </select>
            </div>
          </div>
          <div v-for="field in fields" :key="field.key" class="column is-half">
            <label class="label" :for="`${field.key}-${modalIndex}`">{{
              field.label
            }}</label
            ><input
              :id="`${field.key}-${modalIndex}`"
              v-model="form[field.key]"
              class="input"
              :maxlength="field.max"
              :type="field.key === 'purchased_at' ? 'date' : 'text'"
            />
          </div>
        </div>
        <div class="field">
          <label class="label" :for="`attributes-${modalIndex}`"
            >Attributes (JSON)</label
          ><textarea
            :id="`attributes-${modalIndex}`"
            v-model="attributesText"
            class="textarea"
            rows="3"
            placeholder='{"size_gb": 64}'
          />
        </div>
        <div class="field">
          <label class="label" :for="`note-${modalIndex}`">Note</label
          ><textarea
            :id="`note-${modalIndex}`"
            v-model="form.note"
            class="textarea"
            maxlength="10000"
          />
        </div>
        <p v-if="activeInstallation" class="notification is-info is-light">
          Installed in
          {{
            activeInstallation.camera_serial ||
            activeInstallation.pro_camera_inventory_id
          }}. Remove the component from its camera before changing its status.
        </p>
        <template v-if="modalData.id"
          ><h3 class="title is-5 mt-5">Installation history</h3>
          <installation-history
            :history="history"
            :page="page"
            :loading="loading"
            @change-page="load"
          />
          <div v-if="confirmDelete" class="notification is-warning is-light">
            Delete this unused component? This cannot be undone.
            <button
              class="button is-danger is-small"
              type="button"
              :disabled="isSaving"
              @click="remove"
            >
              Confirm delete
            </button>
            <button
              class="button is-small"
              type="button"
              @click="confirmDelete = false"
            >
              Cancel
            </button>
          </div>
          <button
            v-else
            class="button is-danger is-light mt-4"
            type="button"
            :disabled="history.total_count > 0 || isSaving"
            @click="confirmDelete = true"
          >
            Delete unused component
          </button>
          <p v-if="history.total_count > 0" class="help">
            History is preserved. Use scrapped or lost after removal instead of
            deleting.
          </p></template
        >
      </form>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import { getComponent } from "@/api/components";
import InstallationHistory from "@/components/InstallationHistory.vue";
import toast from "@/utils/toast";
export default {
  name: "ModalComponent",
  components: { InstallationHistory },
  props: {
    modalData: { type: Object, default: () => ({}) },
    modalIndex: { type: Number, required: true },
    isFooterOnly: { type: Boolean, default: false },
  },
  data: () => ({
    form: {
      type: "",
      status: "stock",
      identifier: "",
      label: "",
      manufacturer: "",
      model: "",
      purchased_at: "",
      note: "",
    },
    attributesText: "",
    statuses: ["stock", "installed", "faulty", "repair", "scrapped", "lost"],
    fields: [
      { key: "identifier", label: "Identifier", max: 255 },
      { key: "label", label: "Label", max: 255 },
      { key: "manufacturer", label: "Manufacturer", max: 1000 },
      { key: "model", label: "Model", max: 1000 },
      {
        key: "purchased_at",
        label: "Purchase date",
        max: 64,
      },
    ],
    error: "",
    loading: false,
    formLoaded: false,
    confirmDelete: false,
    page: 1,
    history: { data: [], current: [], total_count: 0, last: 1 },
  }),
  computed: {
    ...mapState(["isSaving"]),
    types() {
      return this.$store.state.componentTypes;
    },
    typesError() {
      return this.$store.state.componentTypesError;
    },
    activeInstallation() {
      return this.history.current?.[0];
    },
  },
  methods: {
    ...mapActions(["closeTopModal"]),
    async load(page = 1) {
      this.loading = true;
      this.error = "";
      try {
        const data = await getComponent(this.modalData.id, { page, size: 10 });
        this.history = data.history;
        this.page = page;
        if (!this.formLoaded) {
          this.form = { ...data.component };
          this.form.purchased_at = data.component.purchased_at?.slice(0, 10) || "";
          this.attributesText =
            data.component.attributes == null
              ? ""
              : JSON.stringify(data.component.attributes, null, 2);
          this.formLoaded = true;
        }
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.isSaving || this.loading) return;
      if (!this.types.includes(this.form.type)) {
        this.error = "Select a component type from the database list.";
        return;
      }
      if (this.modalData.id && !this.formLoaded) {
        this.error =
          "Load the component details before saving. Close and open this window to retry.";
        return;
      }
      this.error = "";
      let attributes;
      try {
        attributes = this.attributesText.trim()
          ? JSON.parse(this.attributesText)
          : null;
      } catch {
        this.error = "Attributes must contain valid JSON.";
        return;
      }
      this.$store.commit("SET_IS_SAVING", true);
      try {
        await this.$store.dispatch("saveComponent", {
          id: this.modalData.id,
          inventoryId: this.modalData.inventoryId,
          data: { ...this.form, purchased_at: this.form.purchased_at || null, attributes },
        });
        toast.success("Component saved");
        this.closeTopModal();
      } catch (e) {
        this.error = e.message;
      } finally {
        this.$store.commit("SET_IS_SAVING", false);
      }
    },
    async remove() {
      this.$store.commit("SET_IS_SAVING", true);
      try {
        await this.$store.dispatch("deleteComponent", this.modalData.id);
        toast.success("Component deleted");
        this.closeTopModal();
      } catch (e) {
        this.error = e.message;
      } finally {
        this.$store.commit("SET_IS_SAVING", false);
      }
    },
  },
  mounted() {
    if (!this.isFooterOnly) {
      this.$store.dispatch("loadComponentTypes");
      this.$root.$on(`component-save-${this.modalIndex}`, this.save);
      if (this.modalData.id) this.load();
    }
  },
  beforeDestroy() {
    if (!this.isFooterOnly)
      this.$root.$off(`component-save-${this.modalIndex}`, this.save);
  },
};
</script>
